import { motion, useReducedMotion } from "framer-motion";

/**
 * TechnologyStrategy — "The Roadmap"
 * A horizontal 5-milestone timeline that illuminates left-to-right. Each
 * milestone briefly reveals a small badge above it describing an outcome.
 * Reflects: a board-ready technology direction, sequenced and measurable.
 */
const STOPS = [
  { x: 60,  label: "ASSESS",     out: "CLARITY" },
  { x: 140, label: "PRIORITISE", out: "FOCUS" },
  { x: 220, label: "INVEST",     out: "RETURN" },
  { x: 300, label: "DELIVER",    out: "MOMENTUM" },
  { x: 360, label: "OUTCOME",    out: "VALUE" },
];

export default function StrategyRoadmap({ className = "" }) {
  const reduce = useReducedMotion();
  const y = 180;

  return (
    <div className={`pointer-events-none select-none ${className}`} aria-hidden="true">
      <svg viewBox="0 0 400 320" className="h-full w-full" role="presentation">
        <defs>
          <linearGradient id="rd-line" x1="0" x2="1">
            <stop offset="0%" stopColor="#7DB4FF" stopOpacity="0.05" />
            <stop offset="50%" stopColor="#3B82F6" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#7DB4FF" stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Baseline (dashed) + animated fill */}
        <line x1="40" y1={y} x2="380" y2={y} stroke="#334155" strokeWidth="1" strokeDasharray="3 4" />
        <motion.line
          x1="40" y1={y} x2="380" y2={y}
          stroke="url(#rd-line)" strokeWidth="1.8" strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={reduce ? { pathLength: 1 } : { pathLength: [0, 1, 1] }}
          transition={reduce ? { duration: 0 } : { duration: 8, times: [0, 0.85, 1], repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Stop markers */}
        {STOPS.map((s, i) => (
          <g key={s.label}>
            {/* Vertical tick */}
            <line x1={s.x} y1={y - 10} x2={s.x} y2={y + 10} stroke="#7DB4FF" strokeOpacity="0.5" />
            {/* Ring + fill circle that illuminates in sequence */}
            <motion.circle
              cx={s.x} cy={y} r={i === STOPS.length - 1 ? 9 : 7}
              fill={i === STOPS.length - 1 ? "#3B82F6" : "#0F172A"}
              stroke={i === STOPS.length - 1 ? "#93C5FD" : "#3B82F6"}
              strokeWidth={i === STOPS.length - 1 ? 1.5 : 1.2}
              initial={{ opacity: 0.4 }}
              animate={reduce ? { opacity: 0.95 } : { opacity: [0.4, 1, 1, 0.4] }}
              transition={reduce ? { duration: 0 } : { duration: 8, delay: i * 0.6, times: [0, 0.4, 0.75, 1], repeat: Infinity, ease: "easeInOut" }}
            />
            {/* Label below */}
            <text x={s.x} y={y + 26} textAnchor="middle" fill="#CBD5E1" fontSize="9" letterSpacing="2" fontFamily="ui-sans-serif, system-ui" style={{ textTransform: "uppercase" }}>
              {s.label}
            </text>

            {/* Outcome badge above — dashed connector + small pill */}
            <line x1={s.x} y1={y - 10} x2={s.x} y2={y - 40} stroke="#3B82F6" strokeOpacity="0.35" strokeDasharray="2 3" />
            <motion.g
              initial={{ opacity: 0, y: -4 }}
              animate={reduce ? { opacity: 1, y: 0 } : { opacity: [0, 1, 1, 0], y: [-4, 0, 0, -4] }}
              transition={reduce ? { duration: 0 } : { duration: 8, delay: i * 0.6, times: [0, 0.45, 0.8, 1], repeat: Infinity, ease: "easeOut" }}
            >
              <rect x={s.x - 30} y={y - 60} width="60" height="16" rx="8" fill="#0F172A" stroke="#3B82F6" strokeOpacity="0.55" />
              <text x={s.x} y={y - 49} textAnchor="middle" fill="#93C5FD" fontSize="8" letterSpacing="2" fontFamily="ui-sans-serif, system-ui" style={{ textTransform: "uppercase" }}>
                {s.out}
              </text>
            </motion.g>
          </g>
        ))}

        {/* Traveling head-of-line pulse */}
        {!reduce && (
          <motion.circle
            r="4" fill="#EFF6FF"
            initial={{ cx: 40, cy: y, opacity: 0 }}
            animate={{ cx: [40, 380, 380], cy: [y, y, y], opacity: [0, 1, 0] }}
            transition={{ duration: 8, times: [0, 0.85, 1], repeat: Infinity, ease: "easeInOut" }}
          />
        )}

        {/* Framing labels */}
        <text x="40"  y={y - 90} fill="#94A3B8" fontSize="9" letterSpacing="2.5" fontFamily="ui-sans-serif, system-ui" style={{ textTransform: "uppercase" }}>
          BOARD-READY DIRECTION
        </text>
        <text x="380" y={y + 90} textAnchor="end" fill="#94A3B8" fontSize="9" letterSpacing="2.5" fontFamily="ui-sans-serif, system-ui" style={{ textTransform: "uppercase" }}>
          MEASURABLE OUTCOME
        </text>
      </svg>
    </div>
  );
}
